import { Switch } from '@mui/joy';

import { useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { CORE_COLUMNS } from '@/Pages/DatasetSessionCompare/utils/columnTypeUtils';
import { Input } from '@/design-system';
import { DatasetSchema, RunFacetField } from '@/types/schema';

import { DragAndDropOrderedTable } from '../../../../components/DragAndDropOrderedTable';
import { useDatasetColumnSuggestionOptions } from '../hooks/useDatasetColumnSuggestionOptions';
import { EditHeatmapPopover } from './EditHeatmapPopover';

export type ExperimentViewEditFormProps = {
  dataset: DatasetSchema;
  rows: ExperimentViewRow[];
  formSessionId?: string;
  experimentFeedbackKeys?: string[];
  facets?: RunFacetField[];
  isLoading?: boolean;
  onChange?: (rows: ExperimentViewRow[]) => void;
  onValidationChange?: (rowId: string, errors: string[]) => void;
  onReset?: () => void;
  noAttachments?: boolean;
};

type ExperimentViewRow = {
  id: string;
  values: {
    name?: string;
    hide?: boolean;
    precision?: number;
  };
  canRemove?: boolean;
  pending?: boolean;
};

const NumberSelect = ({
  min = 1,
  max = 6,
  value,
  onChange,
  ...props
}: {
  min?: number;
  max?: number;
  value?: number;
  onChange?: (value: string | null) => void;
  [key: string]: unknown;
}) => (
  <Input
    type="number"
    size="sm"
    value={value?.toString() ?? ''}
    onChange={(e) => onChange?.(e)}
    placeholder="2"
    className="w-12"
    min={min}
    max={max}
    step={1}
    {...props}
  />
);

export function ExperimentViewEditForm({
  dataset,
  rows,
  facets,
  formSessionId,
  onChange,
  onValidationChange,
  onReset,
  noAttachments,
  isLoading,
}: ExperimentViewEditFormProps) {
  // Calculate initial rows from experiment view
  // Parallel state is sadly necessary here to avoid race conditions with
  // drag and drop rendering.
  const allSuggestionOptions = useDatasetColumnSuggestionOptions(
    dataset,
    facets,
    noAttachments
  );

  // Filter out existing row names from suggestions
  const suggestionOptions = useMemo(() => {
    const existingNames = rows
      .map((row) => row.values.name as string)
      .filter(Boolean);
    return allSuggestionOptions.filter(
      (option) => !existingNames.includes(option)
    );
  }, [allSuggestionOptions, rows]);

  const handleChange = (newRows: ExperimentViewRow[]) => {
    onChange?.(newRows);
  };

  const updateRowField = useDebouncedCallback(
    (rowId: string, field: string, value: any) => {
      const updatedRows = rows.map((row) => {
        return row.id === rowId
          ? { ...row, values: { ...row.values, [field]: value } }
          : row;
      });
      handleChange(updatedRows);
    },
    100
  );

  const handleRemoveColumn = (row: ExperimentViewRow) => {
    // Don't allow deleting core columns, only allow hiding them
    const displayName = row.values.name as string;
    if (CORE_COLUMNS.includes(displayName)) {
      return;
    }
    const updatedRows = rows.filter((r) => r.id !== row.id);
    handleChange(updatedRows);
  };

  const tableColumns = [
    {
      key: 'name',
      header: 'Column Name',
      renderCell: (row) => row.values.name,
    },
    {
      key: 'hide',
      header: 'Visible',
      width: '88px',
      renderCell: (row) => {
        return (
          <Switch
            checked={!row.values.hide}
            onChange={(e) => updateRowField(row.id, 'hide', !e.target.checked)}
            style={{ marginTop: '3px' }}
          />
        );
      },
    },
    {
      key: 'precision',
      header: 'Decimals',
      tooltip:
        'Number of displayed decimal places. Applies only to feedback columns.',
      width: '102px',
      renderCell: (row) => {
        const columnName = row.values.name || '';
        const isFeedbackColumn =
          typeof columnName === 'string' && columnName.startsWith('feedback.');

        return isFeedbackColumn ? (
          <NumberSelect
            value={row.values.precision as number}
            onChange={(value) =>
              updateRowField(
                row.id,
                'precision',
                value ? parseInt(value) : undefined
              )
            }
          />
        ) : null;
      },
    },
    {
      key: 'color_gradient',
      header: 'Heat Map',
      tooltip: 'Color gradient for heat map. Applies only to feedback columns.',
      width: '102px',
      renderCell: (row, i) => {
        const columnName = row.values.name || '';
        const isFeedbackColumn =
          typeof columnName === 'string' && columnName.startsWith('feedback.');
        return isFeedbackColumn ? (
          <EditHeatmapPopover
            key={'heatmap-popover-' + i + '-' + formSessionId}
            feedbackKey={columnName.slice('feedback.'.length)}
            gradient={row.values.color_gradient}
            formSessionId={formSessionId}
            onChange={(gradient) => {
              updateRowField(row.id, 'color_gradient', gradient);
            }}
            onValidationChange={(errors) => {
              onValidationChange?.(row.id, errors);
            }}
          />
        ) : null;
      },
    },
  ];

  return (
    <>
      <DragAndDropOrderedTable
        title="Column Configuration"
        description="Configure columns shown while viewing experiments over this dataset. Columns that are not in this list will appear by default at the end of the experiment table."
        isLoading={isLoading}
        tableColumns={tableColumns}
        rows={rows}
        onChange={handleChange}
        onRemoveRow={handleRemoveColumn}
        placeholder="Column name (e.g. inputs.question)"
        addRowButtonText="Column"
        suggestionOptions={suggestionOptions}
        onReset={onReset}
      />
    </>
  );
}
